/**
 * Shared vocabulary for the asynchronous AI jobs (stem splitter, generations,
 * mood covers).
 *
 * All three endpoints answer with the same envelope: a `status` string and a
 * `responseJson` field holding the provider payload as a JSON *string*. The
 * shape inside it is decided by the provider (Moises, Beatoven, Stable Audio,
 * Kits.ai, Soundberry) and is not part of the API contract, so it is read
 * defensively here rather than by assuming one set of keys.
 */

export const JOB_STATUS = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed',
};

export const JOB_STATUS_LABELS = {
    pending: 'In attesa',
    processing: 'In elaborazione',
    completed: 'Pronta',
    failed: 'Non riuscita',
};

const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled', 'canceled', 'error', 'expired'];

/** A job in a terminal state will never change again: stop polling for it. */
export const isTerminalStatus = (status) => TERMINAL_STATUSES.includes(String(status || '').toLowerCase());

export const hasRunningJob = (jobs = []) => jobs.some(job => !isTerminalStatus(job?.status));

/** `responseJson` is a JSON string, sometimes already an object, often null. */
export const parseJobResponse = (raw) => {
    if (!raw) return {};

    if (typeof raw === 'object') return raw;

    try {
        const parsed = JSON.parse(raw);

        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

const AUDIO_EXTENSIONS = /\.(mp3|wav|flac|ogg|m4a|aac|aiff?)(\?|$)/i;
const isUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

/**
 * Collects every playable result inside a provider payload, whatever shape it
 * came in: `{url}`, `{track_url}`, `{stems: {vocals, drums}}`,
 * `{files: [{name, url}]}` all work.
 *
 * @returns {Array<{label: string|null, url: string}>}
 */
export const extractJobResults = (response) => {
    const results = [];
    const seen = new Set();

    const visit = (node, label) => {
        if (isUrl(node)) {
            if (seen.has(node)) return;

            seen.add(node);
            results.push({ label, url: node });
            return;
        }

        if (Array.isArray(node)) {
            node.forEach(item => visit(item, label));
            return;
        }

        if (node && typeof node === 'object') {
            // A `{name, url}` pair names itself; anything else is named by its key.
            const named = node.name || node.label || node.instrument || node.stem || node.type;

            Object.entries(node).forEach(([key, value]) => {
                if (key === 'error') return;

                visit(value, isUrl(value) ? (named || key) : key);
            });
        }
    };

    visit(parseJobResponse(response), null);

    // Prefer audio files when the payload also carries images or manifests.
    const audio = results.filter(r => AUDIO_EXTENSIONS.test(r.url));

    return audio.length ? audio : results;
}

/** Human readable failure reason, when the provider left one. */
export const extractJobError = (response) => {
    const parsed = parseJobResponse(response);
    const error = parsed.error || parsed.message || parsed.detail;

    return typeof error === 'string' ? error : null;
}
